import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Cookie, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const cookiePolicySections = [
  {
    title: "Cosa sono i cookie",
    body: [
      "I cookie sono piccoli file di testo che i siti web visitati dall’utente inviano al dispositivo utilizzato per la navigazione, dove vengono memorizzati per essere poi ritrasmessi agli stessi siti alla visita successiva.",
      "I cookie possono essere utilizzati per diverse finalità, quali il corretto funzionamento del sito, il miglioramento dell’esperienza di navigazione, l’analisi statistica o la profilazione dell’utente.",
    ],
  },
  {
    title: "Tipologie di cookie utilizzati dal sito",
    body: [
      "Il sito www.zerocinquezero.it utilizza esclusivamente cookie tecnici.",
      "I cookie tecnici sono necessari per consentire il corretto funzionamento del sito e per fornire il servizio richiesto dall’utente.",
      "A titolo esemplificativo, possono essere utilizzati per:",
    ],
    list: [
      "gestire la navigazione tra le pagine;",
      "mantenere impostazioni tecniche della sessione;",
      "garantire la sicurezza del sito;",
      "permettere il corretto caricamento delle pagine;",
      "memorizzare eventuali preferenze tecniche dell’utente.",
    ],
    afterList: [
      "Per l’utilizzo dei cookie tecnici non è richiesto il consenso preventivo dell’utente, ferma restando la necessità di fornire un’adeguata informativa.",
    ],
  },
  {
    title: "Cookie di profilazione",
    body: [
      "Il sito non utilizza cookie di profilazione.",
      "Il sito non utilizza cookie per finalità pubblicitarie, commerciali, comportamentali o di tracciamento dell’utente.",
    ],
  },
  {
    title: "Cookie analitici",
    body: [
      "Il sito non utilizza cookie analitici.",
      "Qualora in futuro fossero introdotti strumenti di analisi statistica, la presente Cookie Policy sarà aggiornata e, ove richiesto dalla normativa applicabile, sarà richiesto il consenso dell’utente.",
    ],
  },
  {
    title: "Cookie di terze parti",
    body: [
      "Il sito non utilizza cookie di terze parti per finalità di profilazione, marketing o tracciamento.",
      "Eventuali componenti tecnici forniti da terzi saranno utilizzati esclusivamente per garantire il funzionamento del sito e non per finalità di tracciamento dell’utente.",
    ],
  },
  {
    title: "Gestione dei cookie tramite browser",
    body: [
      "L’utente può configurare il proprio browser per bloccare, eliminare o limitare l’uso dei cookie.",
      "La disabilitazione dei cookie tecnici può compromettere il corretto funzionamento del sito o di alcune sue funzionalità.",
      "Le impostazioni dei cookie possono essere gestite direttamente dal browser utilizzato dall’utente.",
    ],
  },
  {
    title: "Consenso",
    body: [
      "Poiché il sito utilizza esclusivamente cookie tecnici, non è necessario acquisire il consenso preventivo dell’utente tramite banner cookie.",
      "Nel caso in cui in futuro il sito introduca cookie analitici non anonimizzati, cookie di profilazione o altri strumenti di tracciamento, sarà predisposto un sistema di acquisizione del consenso conforme alla normativa applicabile.",
    ],
  },
  {
    title: "Modifiche alla Cookie Policy",
    body: [
      "Il Titolare si riserva il diritto di modificare o aggiornare la presente Cookie Policy in qualsiasi momento. Le modifiche saranno pubblicate su questa pagina.",
    ],
  },
];

export default function CookiePolicyPage() {
  const [pointerStyle, setPointerStyle] = useState({ "--mouse-x": "50%", "--mouse-y": "18%" });

  useEffect(() => {
    document.title = "Cookie Policy | ZeroCinqueZero";
  }, []);

  const handlePointerMove = (event) => {
    const x = `${Math.round((event.clientX / window.innerWidth) * 100)}%`;
    const y = `${Math.round((event.clientY / window.innerHeight) * 100)}%`;
    setPointerStyle({ "--mouse-x": x, "--mouse-y": y });
  };

  return (
    <div className="app-shell min-h-screen overflow-x-hidden bg-[#050505] text-[#F5F5F7]" style={pointerStyle} onPointerMove={handlePointerMove} data-testid="cookie-policy-page">
      <div className="reactive-background" data-testid="cookie-policy-reactive-background-layer" aria-hidden="true">
        <span className="reactive-glow reactive-glow-primary" />
        <span className="reactive-glow reactive-glow-accent" />
        <span className="reactive-scanline" />
      </div>

      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl" data-testid="cookie-policy-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8 lg:px-12">
          <Link to="/" className="group flex items-center gap-3" data-testid="cookie-policy-logo-home-link" aria-label="Torna alla home ZeroCinqueZero">
            <span className="grid h-10 w-10 place-items-center border border-white/15 bg-white text-sm font-black text-black transition-transform duration-300 group-hover:-translate-y-0.5" data-testid="cookie-policy-logo-mark">
              050
            </span>
            <span className="hidden text-sm font-semibold uppercase tracking-[0.24em] text-white sm:inline" data-testid="cookie-policy-brand-name">
              ZeroCinqueZero
            </span>
          </Link>
          <Button asChild variant="outline" className="h-11 rounded-none border-white/15 bg-transparent px-5 text-white transition-[border-color,transform,background-color] duration-300 hover:-translate-y-0.5 hover:border-white/35 hover:bg-white/10" data-testid="cookie-policy-back-home-button">
            <Link to="/"><ArrowLeft size={17} /> Home</Link>
          </Button>
        </div>
      </header>

      <main className="px-5 pb-16 pt-32 sm:px-8 sm:pb-24 sm:pt-40 lg:px-12" data-testid="cookie-policy-main-content">
        <div className="mx-auto max-w-5xl">
          <section className="mb-12 border border-white/10 bg-[#0F0F13]/80 p-6 shadow-2xl backdrop-blur-xl sm:p-10" data-testid="cookie-policy-hero-card">
            <p className="mb-5 inline-flex items-center gap-3 border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#8B8B96]" data-testid="cookie-policy-eyebrow">
              <Cookie size={18} weight="fill" className="text-[#0047FF]" /> Informativa cookie
            </p>
            <h1 className="text-5xl font-black leading-[0.92] tracking-[-0.065em] text-white sm:text-6xl lg:text-7xl" data-testid="cookie-policy-title">
              Cookie Policy
            </h1>
            <p className="mt-7 max-w-3xl text-base leading-8 text-[#B8B8C2]" data-testid="cookie-policy-intro">
              Informativa sull’uso dei cookie sul sito www.zerocinquezero.it.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 text-sm font-semibold text-white" data-testid="cookie-policy-technical-note">
              <ShieldCheck size={22} weight="fill" className="text-[#FF2E00]" /> Il sito utilizza esclusivamente cookie tecnici.
            </div>
          </section>

          <div className="space-y-5" data-testid="cookie-policy-sections-list">
            {cookiePolicySections.map((section, index) => (
              <article key={section.title} className="border border-white/10 bg-[#0F0F13]/75 p-6 backdrop-blur-xl sm:p-8" data-testid={`cookie-policy-section-${index + 1}`}>
                <h2 className="text-2xl font-bold tracking-[-0.04em] text-white" data-testid={`cookie-policy-section-title-${index + 1}`}>{section.title}</h2>
                <div className="mt-5 space-y-4 text-base leading-8 text-[#B8B8C2]" data-testid={`cookie-policy-section-body-${index + 1}`}>
                  {section.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                  {section.list && (
                    <ul className="space-y-3 pl-5" data-testid={`cookie-policy-section-list-${index + 1}`}>
                      {section.list.map((item) => <li key={item} className="list-disc marker:text-[#0047FF]">{item}</li>)}
                    </ul>
                  )}
                  {section.afterList?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
              </article>
            ))}
          </div>

          <p className="mt-8 border border-[#0047FF]/40 bg-[#0047FF]/10 p-5 text-sm font-semibold uppercase tracking-[0.18em] text-[#DDE6FF]" data-testid="cookie-policy-last-updated">
            Ultimo aggiornamento: 28/04/2026
          </p>
        </div>
      </main>

      <footer className="border-t border-white/10 px-5 py-8 text-sm text-[#8B8B96] sm:px-8 lg:px-12" data-testid="cookie-policy-footer">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="cookie-policy-footer-brand">© ZeroCinqueZero — Software house italiana · P.IVA 04830250231</p>
          <div className="flex flex-wrap gap-5" data-testid="cookie-policy-footer-links">
            <Link to="/privacy-policy" className="transition-colors duration-300 hover:text-white" data-testid="cookie-policy-footer-privacy-link">Privacy</Link>
            <Link to="/" className="transition-colors duration-300 hover:text-white" data-testid="cookie-policy-footer-home-link">Torna alla home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}